import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useLoadedStrategyMetadata } from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import {
  LoadAdjustmentFormTypeEnum,
  type LoadPlanningAdjustmentFormValidated,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import {
  mapToCreateAbsoluteQuantityAdjustmentDTO,
  mapToCreateCompanyHolidaysDTO,
  mapToCreatePercentageQuantityAdjustmentDTO,
  mapToCreateReferenceBaseloadCorrectionDTO,
  mapToCreateRenewableProductionDTO,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/loadPlanning.mapper'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { mapToEnergyTypeDto } from '../../../details/types/adjustment.utils'

export const invalidateLoadPlanningAdjustmentQueries = (queryClient: QueryClient) => {
  // TODO - split this into separate functions for allow only invalidate the adjustments queries
  queryClient.invalidateQueries({
    queryKey: queryKeys.procurement.loadAdjustments.toKey(),
  })
  // TODO - wibi: nuke this code when we cleaned up v2
  queryClient.invalidateQueries({
    queryKey: queryKeys.procurement.loadAdjustmentsV2.toKey(),
  })
  queryClient.invalidateQueries({
    queryKey: queryKeys.procurement.loadPlanning.toKey(),
  })
}

export const useCreateAdjustmentMutation = () => {
  const queryClient = useQueryClient()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()

  return useMutation({
    mutationFn: async (data: LoadPlanningAdjustmentFormValidated) => {
      if (!strategyMetadata) throw new Error(`Strategy metadata not loaded yet`)

      const context = {
        companyId: companyId!,
        energyType: mapToEnergyTypeDto(strategyMetadata.energyType),
        year: String(strategyMetadata.year),
      }

      if (data.type === LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS) {
        const requestBody = mapToCreateCompanyHolidaysDTO(data, context)
        return API.procurements.createCompanyHolidaysAdjustment({ requestBody })
      }

      if (data.type === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT) {
        if (data.adjustmentType === 'percentage') {
          const requestBody = mapToCreatePercentageQuantityAdjustmentDTO(data, context)
          return API.procurements.createPercentageQuantityAdjustment({ requestBody })
        }
        if (data.adjustmentType === 'absolute') {
          const requestBody = mapToCreateAbsoluteQuantityAdjustmentDTO(data, context)
          return API.procurements.createAbsoluteQuantityAdjustment({ requestBody })
        }
      }

      if (data.type === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD) {
        const requestBody = mapToCreateReferenceBaseloadCorrectionDTO(data, context)
        return API.procurements.createReferenceBaseloadCorrection({ requestBody })
      }

      if (data.type === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION) {
        const requestBody = mapToCreateRenewableProductionDTO(data, context)
        return API.procurements.createRenewableProductionAdjustment({ requestBody })
      }

      throw new Error(`No implementation for adjustment type ${data.type}`)
    },
    onSuccess: () => {
      invalidateLoadPlanningAdjustmentQueries(queryClient)
    },
  })
}

type UpdateAdjustmentMutationData = Omit<LoadPlanningAdjustmentFormValidated, 'id'> & {
  id: string
}

export const useUpdateAdjustmentMutation = () => {
  const queryClient = useQueryClient()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()

  return useMutation({
    mutationFn: async (data: UpdateAdjustmentMutationData) => {
      if (!strategyMetadata) throw new Error(`Strategy metadata not loaded yet`)
      if (!data.id) throw new Error('Adjustment id is required for update')

      const context = {
        companyId: companyId!,
        energyType: mapToEnergyTypeDto(strategyMetadata.energyType),
        year: String(strategyMetadata.year),
      }

      if (data.type === LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS) {
        const requestBody = mapToCreateCompanyHolidaysDTO(data, context)
        return API.procurements.updateCompanyHolidaysAdjustment({ id: data.id, requestBody })
      }

      if (data.type === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT) {
        if (data.adjustmentType === 'percentage') {
          const requestBody = mapToCreatePercentageQuantityAdjustmentDTO(data, context)
          return API.procurements.updatePercentageQuantityAdjustment({ id: data.id, requestBody })
        }
        if (data.adjustmentType === 'absolute') {
          const requestBody = mapToCreateAbsoluteQuantityAdjustmentDTO(data, context)
          return API.procurements.updateAbsoluteQuantityAdjustment({ id: data.id, requestBody })
        }
      }

      if (data.type === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD) {
        const requestBody = mapToCreateReferenceBaseloadCorrectionDTO(data, context)
        return API.procurements.updateReferenceBaseloadCorrection({ id: data.id, requestBody })
      }

      if (data.type === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION) {
        const requestBody = mapToCreateRenewableProductionDTO(data, context)
        return API.procurements.updateRenewableProductionAdjustment({ id: data.id, requestBody })
      }

      throw new Error(`No implementation for adjustment type ${data.type}`)
    },
    onSuccess: () => {
      invalidateLoadPlanningAdjustmentQueries(queryClient)
    },
  })
}

export const useDeleteAdjustmentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (adjustmentId: string) => {
      return API.procurements.deleteLoadAdjustment({ id: adjustmentId })
    },
    onSuccess: () => {
      invalidateLoadPlanningAdjustmentQueries(queryClient)
    },
  })
}
